import { useCurrentUser } from '@cma/app'
import { Menu, toast } from '@cma/common'
import { HelixIcon } from '@helix/helix-icon'
import { checkmark, plus } from '@helix/helix-icon/outlined'
import { useContent } from './ContentContext'
import { useMoveFiles } from './useMoveFiles'

interface FolderSubMenuProps {
  fileId: string
  onMenuClose?: () => void
}

export function FolderSubMenu({ fileId, onMenuClose }: FolderSubMenuProps) {
  const { contents, setCreateNewFolder, setMovingFileIds, navigateToFolder } =
    useContent()

  const { data: { currentUser } = {} } = useCurrentUser()
  const { mutate: moveFiles } = useMoveFiles({
    accountId: currentUser?.account?.id || ''
  })

  const currentFile = contents
    .filter((file) => file.__typename === 'PlatformClientContent')
    .find((file) => file.id === fileId)

  const currentFileFolder =
    currentFile?.__typename === 'PlatformClientContent'
      ? currentFile?.folderId || ''
      : ''

  const folders = contents
    ?.filter((content) => content.__typename === 'Folder')
    .map((folder) => ({
      id: folder.id,
      displayName: folder.displayName || 'Folder'
    }))

  const handleMoveToFolder = (targetFolderId: string | null) => {
    setMovingFileIds([fileId])

    if (onMenuClose) onMenuClose()

    moveFiles(
      {
        ids: [fileId],
        folderId: targetFolderId
      },
      {
        onSuccess() {
          const folderName = targetFolderId
            ? folders.find((f) => f.id === targetFolderId)?.displayName ||
              'folder'
            : 'root'
          toast(`File moved to ${folderName} successfully`, {
            variant: 'success'
          })
        },
        onError(error) {
          toast(error.message || 'Failed to move file', {
            variant: 'error'
          })
        },
        onSettled() {
          setMovingFileIds([])
        }
      }
    )
  }

  return (
    <>
      <Menu.Item onClick={() => handleMoveToFolder(null)}>None</Menu.Item>
      {folders
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((folder) => (
          <Menu.Item
            key={folder.id}
            onClick={() => handleMoveToFolder(folder.id)}
            icon={
              currentFileFolder === folder.id ? (
                <HelixIcon icon={checkmark} />
              ) : undefined
            }>
            {folder.displayName}
          </Menu.Item>
        ))}
      <Menu.Item
        icon={<HelixIcon icon={plus} className="h-4 w-4" />}
        onClick={() => {
          onMenuClose?.()
          navigateToFolder(null)
          setCreateNewFolder(true)
          setMovingFileIds([fileId])
        }}>
        Create new folder
      </Menu.Item>
    </>
  )
}
